import {useFlags} from 'launchdarkly-react-client-sdk';
import React, {useCallback, useMemo, useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import {designSystemToken, Headline} from '@lightricks/react-design-system';
import calculateAge from '@/utils/calculateAge';
import extractBackendErrorDetail from '@/utils/extractBackendErrorDetail';
import getAnalyticsScreenName from '@/utils/getAnalyticsScreenName';
import raiseFlashMessage, {
  raiseFlashMessageError,
} from '@/utils/raiseFlashMessage';
import queryClient from '@/utils/reactQueryClient';
import translate from '@/utils/translate';
import PERMISSIONS from '@/config/permissions';
import {ROUTES} from '@/config/routesConstants';
import {
  CreatorProfileAbilities,
  ProfileProps,
  MembershipLabel,
} from '@/types/creatorProfile';
import {MediaFile} from '@/types/mediaFile';
import {
  CREATOR_STATUS,
  type CreatorStatus,
} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import checkPendingNegotiation from '@/views/campaigns/creators-tab/utils/hasPendingNegotiation';
import CreatorAction from '@/views/creators/components/creator-action';
import {
  CREATOR_ACTION_KEYS,
  CreatorActionKey,
} from '@/views/creators/components/creator-action/CreatorAction';
import {CreatorProps} from '@/views/creators/components/creator-action/action/ActionProps';
import AddNoteModal from '@/views/creators/creator-crm/components/add-note-modal';
import Conditional from '@/components/conditional';
import CreatorEmailCopy from '@/components/creator-email-copy';
import CreatorProfileAvatar from '@/components/creator-profile-avatar';
import HeaderActions from '@/components/creator-profile/components/header-actions/HeaderActions';
import type {CtaDescriptor} from '@/components/creator-profile/components/header-actions/HeaderActionsTypes';
import {
  CTA_ID,
  PROFILE_SOURCE,
  getSourceCtaConfig,
  type CtaId,
  type ProfileSource,
} from '@/components/creator-profile/config/profileSources';
import useCreatorCampaignRow from '@/components/creator-profile/hooks/use-creator-campaign-row';
import useProfileSource, {
  PROFILE_SOURCE_QUERY_PARAM,
} from '@/components/creator-profile/hooks/use-profile-source';
import CREATOR_PROFILE_TEST_IDS from '@/components/creator-profile/testIds';
import FlashMessage from '@/components/flash-message';
import Link from '@/components/link';
import NegotiateModal from '@/components/negotiate-modal';
import WithdrawNegotiationOfferModal from '@/components/withdraw-negotiation-offer-modal';
import useTrackRecruitmentActionFlowEvent from '@/hooks/analytics/use-track-recruitment-action-flow-event';
import useCreateBrandSafetyVetting from '@/hooks/mutations/brand-safety/use-create-brand-safety-vetting';
import useRecruitToCrm from '@/hooks/mutations/creators/use-recruit-to-crm';
import useAcceptProposal from '@/hooks/mutations/use-accept-proposal';
import useRejectProposal from '@/hooks/mutations/use-reject-proposal';
import useShortlist from '@/hooks/mutations/use-shortlist-update';
import useAgencyQuery from '@/hooks/queries/use-agency-query';
import useCampaignQuery from '@/hooks/queries/use-campaign-query';
import {CAMPAIGN_V2_CREATORS_COUNTS_BASE_QUERY_KEY} from '@/hooks/queries/use-campaign-v2-creators-counts-query';
import {CAMPAIGN_V2_CREATORS_BASE_QUERY_KEY} from '@/hooks/queries/use-campaign-v2-creators-query';
import useCreatorBrandSafetyVettingsQuery from '@/hooks/queries/use-creator-brand-safety-vettings-query/useCreatorBrandSafetyVettingsQuery';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import {UNIFIED_MESSAGES_BASE_QUERY_KEY} from '@/hooks/queries/use-unified-messages';
import useBrandId from '@/hooks/use-brand-id';
import useDashboardUserPermissions from '@/hooks/use-dashboard-user-permissions';
import useMessagingPanel from '@/hooks/use-messaging-panel';
import useNavigation from '@/hooks/use-navigation';
import useRaiseCreatorAddedToListSuccess from '@/hooks/use-raise-creators-added-to-list-success';
import CreatorProfileInfo from '../creator-profile-info';
import CreatorStatsRow from '../creator-stats-row';
import PlatformFollowers from '../platform-followers';
import styles from './Header.module.scss';

interface HeaderProps {
  profile: ProfileProps;
  creatorProfileAbilities: CreatorProfileAbilities;
  trackButtonPressed: (buttonName: string, overrideParams?: object) => void;
  isModal?: boolean;
}

interface OverrideParams {
  [key: string]: string;
}

function CreatorNameWrap({
  url,
  children,
  trackButtonPressed,
}: {
  url: string | null;
  children: React.ReactNode;
  trackButtonPressed: (buttonName: string, overrideParams?: object) => void;
}) {
  return url ? (
    <Link
      to={url}
      className={styles.link}
      onClick={(event: React.MouseEvent<HTMLAnchorElement>) =>
        trackButtonPressed((event.target as HTMLAnchorElement).innerText, {
          flow_name: 'Open profile page',
        })
      }
    >
      {children}
    </Link>
  ) : (
    children
  );
}

function Header(props: HeaderProps) {
  const brandId = useBrandId();
  const flags = useFlags();
  const {campaignId} = useParams<{campaignId?: string}>();
  const location = useLocation();
  const {navigate} = useNavigation();
  const {dashboardUser} = useDashboardUserQuery({});
  const {
    data: agency,
    currentBrand,
    allowEditCreatorBid,
  } = useAgencyQuery({
    brandId: brandId || '',
  });
  const {campaign} = useCampaignQuery({campaignId});
  const {profile, creatorProfileAbilities, trackButtonPressed, isModal} = props;
  const [activeCreatorAction, setActiveCreatorAction] =
    useState<CreatorActionKey | null>(null);
  const {open: openMessagingPanel} = useMessagingPanel();
  const [isRecruitingToCrm, setIsRecruitingToCrm] = useState(false);
  const [isNegotiateOpen, setIsNegotiateOpen] = useState(false);
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const [isAddNoteOpen, setIsAddNoteOpen] = useState(false);
  const raiseCreatorAddedToListSuccess = useRaiseCreatorAddedToListSuccess();
  const trackRecruitmentActionFlowEvent = useTrackRecruitmentActionFlowEvent();
  const recruitToCrmMutation = useRecruitToCrm();
  const acceptProposal = useAcceptProposal();
  const rejectProposal = useRejectProposal();
  const createVettingMutation = useCreateBrandSafetyVetting({
    brandId: brandId || '',
  });
  const [isVetting, setIsVetting] = useState(false);
  const {createVetting: canCreateVetting} = useDashboardUserPermissions(
    [PERMISSIONS.VETTING.CREATE],
    dashboardUser?.permissions
  );

  const campaignRow = useCreatorCampaignRow(profile.id);
  const source = useProfileSource({
    applicantStatus: campaignRow?.applicantStatus,
    displayPublicDataOnly: creatorProfileAbilities.displayPublicDataOnly,
  });
  // In the CRM (agency) context, fall back to a sibling org brand's report
  // when the current brand has none. Decision stays per-brand.
  const scope = source === PROFILE_SOURCE.CRM ? 'agency' : undefined;

  const {data: vettings} = useCreatorBrandSafetyVettingsQuery({
    brandId: brandId || '',
    profileId: profile.id,
    enabled: !!brandId,
    scope,
  });

  const safeCollabStatus = useMemo(() => {
    if (!vettings) return null;
    return vettings[0]?.status ?? null;
  }, [vettings]);

  const vettingsLoaded = !!vettings;
  const creatorAlreadyVetted = (vettings?.length ?? 0) > 0;

  /* ====== Analytics functions ====== */
  const analyticsSource = `creator profile ${isModal ? 'modal' : 'page'}`;
  const trackRecruitmentEventStarted = (actionName: string) => {
    const screenName = getAnalyticsScreenName(window.location.pathname);
    trackRecruitmentActionFlowEvent.started({
      flow_name: actionName,
      action_name: actionName,
      screen_name: screenName,
      recruitment_search_id: '',
      creator_id: profile.id,
      source: analyticsSource,
    });
  };

  const trackRecruitmentEventEnded = (
    actionName: string,
    reason: string,
    overrideParams = {}
  ) => {
    const screenName = getAnalyticsScreenName(window.location.pathname);
    trackRecruitmentActionFlowEvent.ended({
      flow_name: actionName,
      action_id: actionName || '',
      screen_name: screenName,
      recruitment_search_id: '',
      creator_id: profile.id,
      reason,
      source: analyticsSource,
      ...overrideParams,
    });
  };

  /* ====== Creator labels calculations ====== */
  const currentBrandMembershipLabels = useMemo(() => {
    if (profile.currentBrandMembership) {
      return profile.currentBrandMembership.labels.map((label) => ({
        ...label,
        value: label.id,
        label: label.name,
        disabled: true,
      }));
    }
    return [];
  }, [profile.currentBrandMembership]);

  const profileForCreatorAction = useMemo(() => {
    return {
      ...profile,
      age: calculateAge(profile.birthDate),
      name: profile.displayName,
      avatar: profile.imageUrl,
    };
  }, [profile]);

  /* ====== Handlers ====== */
  const onMessageClicked = () => {
    trackButtonPressed('Message');
    trackRecruitmentEventStarted(CREATOR_ACTION_KEYS.MESSAGE_CREATOR);
    openMessagingPanel(profile.id);
  };

  const onAddToListClicked = () => {
    trackButtonPressed('Add to list');
    setActiveCreatorAction(CREATOR_ACTION_KEYS.ADD_TO_MY_CREATORS);
  };

  const onInviteToCampaignClicked = (label: string) => {
    trackButtonPressed(label);
    setActiveCreatorAction(CREATOR_ACTION_KEYS.ADD_TO_CAMPAIGN);
  };

  const onShareClicked = () => {
    trackButtonPressed('Share');
    setActiveCreatorAction(CREATOR_ACTION_KEYS.CREATE_AND_SHARE_LIST);
  };

  const onCloseCreatorAction = (
    reason: 'success' | 'cancelled',
    creators?: CreatorProps[],
    actionName?: string,
    result?: unknown
  ) => {
    const overrideParams: OverrideParams = {};
    if (reason === 'success') {
      if (actionName === CREATOR_ACTION_KEYS.ADD_TO_MY_CREATORS) {
        raiseCreatorAddedToListSuccess(
          (result as MembershipLabel[]).filter(
            (label: MembershipLabel & {disabled?: boolean}) => !label.disabled
          )
        );
      }
      if (
        [
          CREATOR_ACTION_KEYS.MESSAGE_CREATOR,
          CREATOR_ACTION_KEYS.ADD_TO_MY_CREATORS,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
        ].includes(actionName)
      ) {
        // Invalidate the query and refetch the creator profile — some actions
        // (e.g. sending a message) change derived UI state like the
        // "Message" button label.
        queryClient.invalidateQueries([
          'creatorProfile',
          creatorProfileAbilities.displayPublicDataOnly,
          profile.id,
          brandId,
        ]);
      }
      if (actionName === CREATOR_ACTION_KEYS.ADD_TO_CAMPAIGN) {
        overrideParams.campaign_id = result as string;
      }
    }
    trackRecruitmentEventEnded(actionName || '', reason, overrideParams);
    setActiveCreatorAction(null);
  };

  const handlePresentedOrDismissed = (
    presentedOrDismissed: 'presented' | 'dismissed',
    actionName: string,
    creators?: CreatorProps[]
  ) => {
    if (presentedOrDismissed === 'presented') {
      trackRecruitmentEventStarted(actionName);
    }
  };

  const showRecruitToCrmSuccess = useCallback(() => {
    raiseFlashMessage({
      status: 'success',
      mode: 'light',
      message: (
        <Headline size="xs" width="100%" display="flex" justifyContent="center">
          {translate('creator-recruited-to-crm')}
        </Headline>
      ),
      withCloseButton: false,
      style: {justifyContent: 'center'},
    });
  }, []);

  const showRecruitToCrmError = useCallback(() => {
    raiseFlashMessageError({
      title: translate('creator-recruit-to-crm-error'),
      subtitle: translate('errors.please-try-again-or-come-back-later'),
    });
  }, []);

  const onRecruitToCrmClicked = useCallback(async () => {
    trackButtonPressed('Recruit to CRM');
    const agencyId = agency?.id;

    if (!agencyId) {
      showRecruitToCrmError();
      return;
    }

    setIsRecruitingToCrm(true);
    let isError = false;

    try {
      const response = await recruitToCrmMutation.mutateAsync({
        agencyId,
        creatorIds: [profile.id],
      });

      const result = response.results.profiles[0];
      if (result?.success) {
        showRecruitToCrmSuccess();
        queryClient.invalidateQueries(['creatorProfile']);
        queryClient.invalidateQueries(['sharedListCreatorProfile']);
      } else {
        isError = true;
      }
    } catch (error) {
      isError = true;
    }

    setIsRecruitingToCrm(false);

    if (isError) {
      showRecruitToCrmError();
    }
  }, [
    trackButtonPressed,
    recruitToCrmMutation,
    agency?.id,
    profile.id,
    showRecruitToCrmSuccess,
    showRecruitToCrmError,
  ]);

  const onVetClicked = async () => {
    trackButtonPressed('Vet');
    setIsVetting(true);

    try {
      const fullName =
        `${profile.firstName || ''} ${profile.lastName || ''}`.trim() ||
        profile.displayName;

      await createVettingMutation.mutateAsync({
        creator: {
          id: profile.id,
          fullName,
          email: profile.email,
          emails: profile.email ? [profile.email] : null,
          profileImage: profile.imageUrl,
        },
        brandId,
        type: 'on_platform',
      });

      raiseFlashMessage({
        status: 'success',
        mode: 'light',
        message: (
          <Headline
            size="xs"
            width="100%"
            display="flex"
            justifyContent="center"
          >
            {translate('Vetting started successfully')}
          </Headline>
        ),
        withCloseButton: false,
        style: {justifyContent: 'center'},
      });
    } catch (error) {
      raiseFlashMessageError({
        title: translate('Failed to start vetting'),
        subtitle: translate('errors.please-try-again-or-come-back-later'),
      });
    }

    setIsVetting(false);
  };

  const paidGigId = campaignRow?.paidGigId ?? null;
  const hasPendingNegotiation = checkPendingNegotiation(campaignRow);
  const brandNegotiationId =
    campaignRow?.brandNegotiationSnapshot?.brandNegotiation?.id ?? null;
  const negotiationSnapshot = campaignRow?.brandNegotiationSnapshot ?? null;

  // Mirrors the visibility rules in useCreatorRowMenuItems — keep them in
  // sync until both consumers are folded into a shared hook.
  const isApplicationCreator =
    !!paidGigId && campaignRow?.applicantStatus === CREATOR_STATUS.Applications;
  const canNegotiateApplication =
    isApplicationCreator &&
    !campaign?.hideInfluencerPricing &&
    !!allowEditCreatorBid;
  const isNegotiable = canNegotiateApplication && !hasPendingNegotiation;
  const canWithdrawOffer =
    canNegotiateApplication && hasPendingNegotiation && !!negotiationSnapshot;

  // useShortlist takes an array (to build its paidGigId map); pass a stable
  // single-element one.
  const shortlistRows = useMemo(
    () => (campaignRow ? [campaignRow] : []),
    [campaignRow]
  );
  const shortlist = useShortlist(campaignId, shortlistRows);
  const isShortlisted = campaignRow
    ? shortlist.isShortlisted(campaignRow.id)
    : false;
  const onShortlistClicked = () => {
    if (!campaignRow) return;
    trackButtonPressed(
      isShortlisted ? 'Remove from shortlist' : 'Add to shortlist'
    );
    shortlist.toggle(campaignRow.id);
  };

  // Same keys the row menu invalidates so the snapshot + counts sync up.
  const invalidateNegotiationCaches = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [UNIFIED_MESSAGES_BASE_QUERY_KEY],
    });
    queryClient.invalidateQueries({
      queryKey: [CAMPAIGN_V2_CREATORS_BASE_QUERY_KEY],
    });
  }, []);

  const onNegotiateClicked = () => {
    if (!paidGigId) return;
    trackButtonPressed('Negotiate');
    setIsNegotiateOpen(true);
  };

  const onWithdrawClicked = () => {
    if (!canWithdrawOffer) return;
    trackButtonPressed('Withdraw Offer');
    setIsWithdrawOpen(true);
  };

  const onAddNoteClicked = () => {
    trackButtonPressed('Add note');
    setIsAddNoteOpen(true);
  };

  // Post-mutation: rewrite location.state + ?source= so the next render
  // picks up the new applicantStatus and shows the matching CTA layout.
  // Do NOT rewrite ?creatorStatus= — it identifies the originating tab and
  // is read by the in-app back button.
  const replaceApplicantStatusInNavState = useCallback(
    (nextStatus: CreatorStatus, nextSource: ProfileSource) => {
      if (!campaignRow) return;
      const nextSearch = new URLSearchParams(location.search);
      nextSearch.set(PROFILE_SOURCE_QUERY_PARAM, nextSource);
      navigate(`${location.pathname}?${nextSearch.toString()}`, {
        replace: true,
        state: {
          campaignRow: {...campaignRow, applicantStatus: nextStatus},
        },
      });
    },
    [campaignRow, location.pathname, location.search, navigate]
  );

  const onAcceptClicked = () => {
    if (!paidGigId) return;
    trackButtonPressed('Accept');
    acceptProposal.mutate(
      {paidGigId},
      {
        onSuccess: () => {
          raiseFlashMessage({
            status: 'success',
            mode: 'light',
            message: (
              <FlashMessage.Banner
                title={translate('Creator accepted')}
                subtitle={profile.displayName}
              />
            ),
          });
          queryClient.invalidateQueries({
            queryKey: [CAMPAIGN_V2_CREATORS_BASE_QUERY_KEY],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: [CAMPAIGN_V2_CREATORS_COUNTS_BASE_QUERY_KEY],
            exact: false,
          });
          replaceApplicantStatusInNavState(
            CREATOR_STATUS.Hired,
            PROFILE_SOURCE.ACCEPTED
          );
        },
        onError: (error: unknown) => {
          raiseFlashMessageError({
            title: translate('Failed to accept creator'),
            subtitle: extractBackendErrorDetail(error),
          });
        },
      }
    );
  };

  const onRejectClicked = () => {
    if (!paidGigId) return;
    trackButtonPressed('Reject');
    rejectProposal.mutate(
      {paidGigId},
      {
        onSuccess: () => {
          raiseFlashMessage({
            status: 'success',
            mode: 'light',
            message: (
              <FlashMessage.Banner
                title={translate('Creator rejected')}
                subtitle={profile.displayName}
              />
            ),
          });
          queryClient.invalidateQueries({
            queryKey: [CAMPAIGN_V2_CREATORS_BASE_QUERY_KEY],
            exact: false,
          });
          queryClient.invalidateQueries({
            queryKey: [CAMPAIGN_V2_CREATORS_COUNTS_BASE_QUERY_KEY],
            exact: false,
          });
          replaceApplicantStatusInNavState(
            CREATOR_STATUS.Declined,
            PROFILE_SOURCE.DECLINED
          );
        },
        onError: (error: unknown) => {
          raiseFlashMessageError({
            title: translate('Failed to reject creator'),
            subtitle: extractBackendErrorDetail(error),
          });
        },
      }
    );
  };

  const showVetButton = canCreateVetting && !creatorAlreadyVetted;
  const showRecruitToCrmCta = !!flags.fifaRevamp && !!dashboardUser?.agency?.id;
  const isAlreadyInCrm = !!profile.crmRecruitmentStatus?.recruited;

  const ctaDescriptors: Record<CtaId, CtaDescriptor> = {
    [CTA_ID.INVITE_TO_CAMPAIGN]: {
      id: CTA_ID.INVITE_TO_CAMPAIGN,
      label: translate('Invite to campaign'),
      testID: CREATOR_PROFILE_TEST_IDS.HEADER__INVITE_TO_CAMPAIGN_BUTTON,
      onClick: () => onInviteToCampaignClicked('Invite to campaign'),
      visible: creatorProfileAbilities.showInviteToCampaignButton,
    },
    [CTA_ID.INVITE_TO_ANOTHER_CAMPAIGN]: {
      id: CTA_ID.INVITE_TO_ANOTHER_CAMPAIGN,
      label: translate('Invite to another campaign'),
      onClick: () => onInviteToCampaignClicked('Invite to another campaign'),
      visible: creatorProfileAbilities.showInviteToCampaignButton,
    },
    [CTA_ID.INVITE_TO_NEXT_CAMPAIGN]: {
      id: CTA_ID.INVITE_TO_NEXT_CAMPAIGN,
      label: translate('Invite to next campaign'),
      onClick: () => onInviteToCampaignClicked('Invite to next campaign'),
      visible: creatorProfileAbilities.showInviteToCampaignButton,
    },
    [CTA_ID.ADD_TO_LIST]: {
      id: CTA_ID.ADD_TO_LIST,
      label: translate('Add to list'),
      testID: CREATOR_PROFILE_TEST_IDS.HEADER__ADD_TO_LIST_BUTTON,
      icon: 'Actions-Bookmark',
      onClick: onAddToListClicked,
      visible: creatorProfileAbilities.showAddToMyCreatorsButton,
    },
    [CTA_ID.MESSAGE]: {
      id: CTA_ID.MESSAGE,
      label: translate('message-button'),
      testID: CREATOR_PROFILE_TEST_IDS.HEADER__MESSAGE_BUTTON,
      icon: 'Navigation-Mail',
      onClick: onMessageClicked,
      visible: creatorProfileAbilities.showCollaborationButton,
    },
    [CTA_ID.VET]: {
      id: CTA_ID.VET,
      label: translate('Vet'),
      testID: CREATOR_PROFILE_TEST_IDS.HEADER__VET_BUTTON,
      icon: 'Features-Eye',
      onClick: onVetClicked,
      visible: showVetButton,
      isLoading: isVetting || !vettingsLoaded,
    },
    [CTA_ID.RECRUIT_TO_CRM]: {
      id: CTA_ID.RECRUIT_TO_CRM,
      label: isAlreadyInCrm
        ? translate('In CRM')
        : translate('creator-card-overflow-menu-recruit-to-crm'),
      testID: CREATOR_PROFILE_TEST_IDS.HEADER__RECRUIT_TO_CRM_BUTTON,
      icon: 'Navigation-Creator-Add',
      onClick: isAlreadyInCrm ? () => {} : onRecruitToCrmClicked,
      disabled: isAlreadyInCrm || isRecruitingToCrm,
      isLoading: isRecruitingToCrm,
      visible: showRecruitToCrmCta,
    },
    [CTA_ID.SHARE]: {
      id: CTA_ID.SHARE,
      label: translate('Share'),
      icon: 'Actions-Share',
      onClick: onShareClicked,
      visible: true,
    },
    [CTA_ID.ADD_NOTE]: {
      id: CTA_ID.ADD_NOTE,
      label: translate('Add note'),
      icon: 'Features-Subtitles',
      onClick: onAddNoteClicked,
      visible: true,
    },
    [CTA_ID.ACCEPT]: {
      id: CTA_ID.ACCEPT,
      label: translate('Accept'),
      onClick: onAcceptClicked,
      visible: !!paidGigId,
      disabled: !paidGigId || acceptProposal.isLoading || hasPendingNegotiation,
      isLoading: acceptProposal.isLoading,
      tooltip: hasPendingNegotiation
        ? translate(
            'Please resolve the outstanding offer prior to accepting this creator application.'
          )
        : undefined,
    },
    [CTA_ID.REJECT]: {
      id: CTA_ID.REJECT,
      label: translate('Reject'),
      onClick: onRejectClicked,
      visible: !!paidGigId,
      disabled: !paidGigId || rejectProposal.isLoading,
      isLoading: rejectProposal.isLoading,
    },
    [CTA_ID.NEGOTIATE]: {
      id: CTA_ID.NEGOTIATE,
      label: translate('Negotiate'),
      onClick: onNegotiateClicked,
      visible: isNegotiable,
    },
    [CTA_ID.WITHDRAW_OFFER]: {
      id: CTA_ID.WITHDRAW_OFFER,
      label: translate('Withdraw Offer'),
      onClick: onWithdrawClicked,
      visible: canWithdrawOffer,
    },
    [CTA_ID.ADD_TO_SHORTLIST]: {
      id: CTA_ID.ADD_TO_SHORTLIST,
      label: isShortlisted
        ? translate('Remove from shortlist')
        : translate('Add to shortlist'),
      icon: isShortlisted ? 'Navigation-Heart-Fill' : 'Navigation-Heart-Line',
      onClick: onShortlistClicked,
      visible: !!campaignRow,
    },
  };

  // source === null → no action row (public profile, shared list, unresolved
  // campaign context).
  const sourceConfig = source ? getSourceCtaConfig(source) : null;
  const primary = sourceConfig
    ? ctaDescriptors[sourceConfig.primary as CtaId]
    : undefined;
  const secondary = sourceConfig
    ? sourceConfig.secondary.map((id) => ctaDescriptors[id as CtaId])
    : [];
  const overflow = sourceConfig
    ? sourceConfig.overflow.map((id) => ctaDescriptors[id as CtaId])
    : [];

  /* ====== Render ====== */
  return (
    <>
      <CreatorAction
        creators={[profileForCreatorAction]}
        activeCreatorAction={activeCreatorAction}
        close={onCloseCreatorAction}
        onPresentedOrDismissed={handlePresentedOrDismissed}
        initState={{
          [CREATOR_ACTION_KEYS.ADD_TO_MY_CREATORS]:
            currentBrandMembershipLabels,
        }}
      />
      {isNegotiateOpen ? (
        <NegotiateModal
          open
          onClose={() => setIsNegotiateOpen(false)}
          paidGigId={paidGigId}
          campaignId={campaignId}
          onSuccess={invalidateNegotiationCaches}
        />
      ) : null}
      {isWithdrawOpen && brandNegotiationId && negotiationSnapshot ? (
        <WithdrawNegotiationOfferModal
          open
          onClose={() => setIsWithdrawOpen(false)}
          brandNegotiationId={brandNegotiationId}
          paidGigId={paidGigId}
          networkPrettyName={campaign?.campaignNetworkPrettyName}
          snapshot={negotiationSnapshot}
          onWithdrawSuccess={() => {
            setIsWithdrawOpen(false);
            invalidateNegotiationCaches();
          }}
        />
      ) : null}
      {isAddNoteOpen ? (
        <AddNoteModal
          open
          onClose={() => setIsAddNoteOpen(false)}
          creatorId={profile.id}
          creatorName={profile.displayName}
        />
      ) : null}
      <div className={styles.headerContainer}>
        <div className={styles.avatarSection}>
          <CreatorProfileAvatar
            className={styles.headerAvatar}
            profileImageUrl={profile.imageUrl}
            profileDisplayName={profile.displayName}
            size="3xl"
            allowEnlarge
            onClick={(mediaFile: MediaFile) => {
              trackButtonPressed('Open creator avatar', {
                flow_id: mediaFile.kind,
                flow_name: mediaFile.url,
              });
            }}
          />
        </div>
        <div className={styles.contentSection}>
          <div className={styles.topRow}>
            <div className={styles.nameColumn}>
              <div className={styles.nameAndInfo}>
                <CreatorNameWrap
                  url={
                    isModal
                      ? `${ROUTES.CREATOR_PROFILE}/${profile.id}?brandId=${brandId}`
                      : null
                  }
                  trackButtonPressed={trackButtonPressed}
                >
                  <Headline
                    data-testid={CREATOR_PROFILE_TEST_IDS.HEADER__DISPLAY_NAME}
                    size="lg"
                    color={designSystemToken('semantic.fg.primary')}
                  >
                    {profile.displayName}
                  </Headline>
                </CreatorNameWrap>
                <CreatorProfileInfo
                  testID={CREATOR_PROFILE_TEST_IDS.HEADER__PROFILE_INFO}
                  profile={profile}
                />
                <CreatorEmailCopy email={profile.email} testID="header" />
              </div>
              <CreatorStatsRow
                profile={profile}
                showAuthenticity={false}
                showRating={!creatorProfileAbilities.displayPublicDataOnly}
                showGigCount={
                  creatorProfileAbilities.displayPublicDataOnly ||
                  !!profile.completedPaidGigsCount
                }
                safeCollabStatus={safeCollabStatus}
              />
            </div>
            <Conditional
              condition={!creatorProfileAbilities.displayPublicDataOnly}
            >
              <PlatformFollowers identities={profile.identities || []} />
            </Conditional>
          </div>
          <Conditional condition={!!sourceConfig}>
            <HeaderActions
              primary={primary}
              secondary={secondary}
              overflow={overflow}
            />
          </Conditional>
        </div>
      </div>
    </>
  );
}

export default Header;
